import Alpine from 'alpinejs'
import Chart from 'chart.js/auto'

document.addEventListener('alpine:init', () => {
  Alpine.data('heatDemandScenarioOutputs', () => {
    const dwellingTypes = ['Detached', 'Semi-detached', 'Terraced', 'Flat']
    let consumptionChart
    let consumptionData // To hold chart data
    let heatSupplyChart
    let heatSupplyData // To hold chart data
    let halfHourlyConsumptionChart
    let halfHourlyConsumptionData
    let halfHourlySupplyChart
    let halfHourlySupplyData
    let halfHourlyCombinedChart
    let halfHourlyCombinedData

    // Destroy the charts before swapping to ensure we don't get phantom chart resizes
    document.addEventListener('htmx:beforeSwap', function (event) {
      if (consumptionChart) {
        consumptionChart.destroy()
        consumptionChart = null
      }
      if (halfHourlyConsumptionChart) {
        halfHourlyConsumptionChart.destroy()
        halfHourlyConsumptionChart = null
      }
    })

    return {
      data: null,
      loading: false,
      halfHourlyConsumptionZoomLevel: 1,
      halfHourlySupplyZoomLevel: 1,
      halfHourlyCombinedZoomLevel: 1,

      resetZoom (e) {
        halfHourlyCombinedChart.resetZoom()
        halfHourlySupplyChart.resetZoom()
        halfHourlyConsumptionChart.resetZoom()
      },

      init () {
        setTimeout(() => this.doBindings(), 100)
      },

      doBindings () {
        const store = Alpine.store('scenarioState')
        // Initialise the charts
        consumptionChart = new Chart(document.getElementById('consumptionChart'), {
          type: 'bar',
          data: {
            labels: dwellingTypes,
            datasets: [{ order: 2 }, { order: 1, pointRadius: 10, borderWidth: 0 }]
          },
          options: {
            maintainAspectRatio: false,
            scales: {
              y: {
                display: false
              },
              consumptionAnnual: {
                position: 'left',
                title: {
                  display: true,
                  text: 'Annual electricity consumption (kWh)'
                }
              },
              consumptionPeak: {
                position: 'right',
                title: {
                  display: true,
                  text: 'Peak electricity consumption (kW)'
                }
              }
            },
            plugins: {
              legend: {
                display: true
              },
              tooltip: {
                callbacks: {
                  label: context => {
                    const label = context.dataset.label || ''
                    const value = context.parsed.y || 0
                    return `${label}: ${Math.round(value).toLocaleString()}`
                  }
                }
              }
            }
          }
        })
        consumptionData = document.getElementById('consumptionChart-data')

        heatSupplyChart = new Chart(document.getElementById('heatSupplyChart'), {
          type: 'bar',
          data: {
            labels: dwellingTypes,
            datasets: [{ order: 2 }, { order: 1, pointRadius: 10, borderWidth: 0 }]
          },
          options: {
            maintainAspectRatio: false,
            scales: {
              y: {
                display: false
              },
              consumptionAnnual: {
                position: 'left',
                title: {
                  display: true
                }
              },
              consumptionPeak: {
                position: 'right',
                title: {
                  display: true
                }
              }
            },
            plugins: {
              legend: {
                display: true
              },
              tooltip: {
                callbacks: {
                  label: context => {
                    const label = context.dataset.label || ''
                    const value = context.parsed.y || 0
                    return `${label}: ${Math.round(value).toLocaleString()}`
                  }
                }
              }
            }
          }
        })
        heatSupplyData = document.getElementById('heatSupplyChart-data')

        halfHourlyConsumptionChart = new Chart(document.getElementById('halfHourlyConsumption'), {
          type: 'line',
          options: {
            maintainAspectRatio: false,
            interaction: {
              mode: 'nearest',
              axis: 'x',
              intersect: false
            },
            elements: {
              line: {
                tension: 0.4,
                borderWidth: 1
              },
              point: {
                radius: 0
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Electricity consumption (MW)'
                }
              },
              x: {
                title: {
                  display: true,
                  text: 'Date'
                }
              }
            },
            plugins: {
              tooltip: {
                callbacks: {
                  label: context => {
                    const label = context.dataset.label || ''
                    const value = context.parsed.y || 0
                    return `${label}: ${Math.round(value).toLocaleString()} MW`
                  }
                }
              },
              zoom: {
                pan: {
                  enabled: true,
                  mode: 'x',
                  modifierKey: 'shift'
                },
                zoom: {
                  wheel: {
                    enabled: true
                  },
                  pinch: {
                    enabled: true
                  },
                  drag: {
                    enabled: true
                  },
                  mode: 'x',
                  onZoomComplete: (chart) => {
                    this.halfHourlyConsumptionZoomLevel = halfHourlyConsumptionChart.getZoomLevel()
                  }
                }
              }
            }
          }
        })
        halfHourlyConsumptionData = document.getElementById('halfHourlyConsumption-data')

        halfHourlySupplyChart = new Chart(document.getElementById('halfHourlySupply'), {
          type: 'line',
          options: {
            maintainAspectRatio: false,
            interaction: {
              mode: 'nearest',
              axis: 'x',
              intersect: false
            },
            elements: {
              line: {
                tension: 0.4,
                borderWidth: 1
              },
              point: {
                radius: 0
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Heat supplied (MW)'
                }
              },
              x: {
                title: {
                  display: true,
                  text: 'Date'
                }
              }
            },
            plugins: {
              tooltip: {
                callbacks: {
                  label: context => {
                    const label = context.dataset.label || ''
                    const value = context.parsed.y || 0
                    return `${label}: ${Math.round(value).toLocaleString()} MW`
                  }
                }
              },
              zoom: {
                pan: {
                  enabled: true,
                  mode: 'x',
                  modifierKey: 'shift'
                },
                zoom: {
                  wheel: {
                    enabled: true
                  },
                  pinch: {
                    enabled: true
                  },
                  drag: {
                    enabled: true
                  },
                  mode: 'x',
                  onZoomComplete: (chart) => {
                    this.halfHourlySupplyZoomLevel = halfHourlySupplyChart.getZoomLevel()
                  }
                }
              }
            }
          }
        })
        halfHourlySupplyData = document.getElementById('halfHourlySupply-data')

        halfHourlyCombinedChart = new Chart(document.getElementById('halfHourlyCombined'), {
          type: 'line',
          options: {
            maintainAspectRatio: false,
            interaction: {
              mode: 'nearest',
              axis: 'x',
              intersect: false
            },
            elements: {
              line: {
                tension: 0.4,
                borderWidth: 1
              },
              point: {
                radius: 0
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Power (MW)'
                }
              },
              x: {
                title: {
                  display: true,
                  text: 'Date'
                }
              }
            },
            plugins: {
              tooltip: {
                callbacks: {
                  label: context => {
                    const label = context.dataset.label || ''
                    const value = context.parsed.y || 0
                    return `${label}: ${Math.round(value).toLocaleString()} MW`
                  }
                }
              },
              zoom: {
                pan: {
                  enabled: true,
                  mode: 'x',
                  modifierKey: 'shift'
                },
                zoom: {
                  wheel: {
                    enabled: true
                  },
                  pinch: {
                    enabled: true
                  },
                  drag: {
                    enabled: true
                  },
                  mode: 'x',
                  onZoomComplete: (chart) => {
                    this.halfHourlyCombinedZoomLevel = halfHourlyCombinedChart.getZoomLevel()
                  }
                }
              }
            }
          }
        })
        halfHourlyCombinedData = document.getElementById('halfHourlyCombined-data')

        // Update consumption labels depending on zoom level
        Alpine.effect(() => {
          if (!this.data) return
          if (this.halfHourlyConsumptionZoomLevel > 8) { halfHourlyConsumptionChart.data.labels = this.data.halfHourlyConsumption.map(entry => new Date(entry.timestamp).toLocaleString()) } else { halfHourlyConsumptionChart.data.labels = this.data.halfHourlyConsumption.map(entry => new Date(entry.timestamp).toLocaleDateString()) }
          halfHourlyConsumptionChart.update()
        })

        // Update supply labels depending on zoom level
        Alpine.effect(() => {
          if (!this.data) return
          if (this.halfHourlySupplyZoomLevel > 8) { halfHourlySupplyChart.data.labels = this.data.halfHourlyHeatSupply.map(entry => new Date(entry.timestamp).toLocaleString()) } else { halfHourlySupplyChart.data.labels = this.data.halfHourlyHeatSupply.map(entry => new Date(entry.timestamp).toLocaleDateString()) }
          halfHourlySupplyChart.update()
        })

        // Update combined labels depending on zoom level
        Alpine.effect(() => {
          if (!this.data) return
          if (this.halfHourlyCombinedZoomLevel > 8) { halfHourlyCombinedChart.data.labels = this.data.halfHourlyHeatSupply.map(entry => new Date(entry.timestamp).toLocaleString()) } else { halfHourlyCombinedChart.data.labels = this.data.halfHourlyHeatSupply.map(entry => new Date(entry.timestamp).toLocaleDateString()) }
          halfHourlyCombinedChart.update()
        })

        // As selection changes, fetch data for newly selected region
        Alpine.effect(() => {
          const loc = window.location.href
          let region = 'GB'
          if (store.selectedSpatialResolution === 'Country') {
            region = store.selectedCountry
          }
          if (store.selectedSpatialResolution === 'LA') {
            region = store.selectedLA
          }
          this.loading = true
          fetch(`${loc}/${region}/${store.selectedYear}/data.json`).then(response => response.json()).then(data => {
            this.loading = false
            this.data = data
          })
        })

        // As loaded data changes, update charts
        Alpine.effect(() => {
          if (!this.data) return
          const magntitudesAnnual = {
            GB: { unit: 'GWh', factor: 1000000 },
            Country: { unit: 'GWh', factor: 1000000 },
            LA: { unit: 'MWh', factor: 1000 }
          }
          const magntitudesPeak = {
            GB: { unit: 'MW', factor: 1000 },
            Country: { unit: 'MW', factor: 1000 },
            LA: { unit: 'kW', factor: 1 }
          }

          // Metrics chart
          const annualConsumptionCounts = {}
          const peakConsumptionCounts = {}
          const annualHeatDemandCounts = {}
          const peakHeatDemandCounts = {}
          this.data.heatPumpMetrics.forEach(entry => {
            annualConsumptionCounts[entry.Built_form] = parseFloat(entry.Annual_electricity_consumption_HPs) / magntitudesAnnual[store.selectedSpatialResolution].factor
            peakConsumptionCounts[entry.Built_form] = parseFloat(entry.Half_hourly_peak_electricity_consumption_HPs) / magntitudesPeak[store.selectedSpatialResolution].factor
            annualHeatDemandCounts[entry.Built_form] = parseFloat(entry.Annual_heat_supplied_HPs) / magntitudesAnnual[store.selectedSpatialResolution].factor
            peakHeatDemandCounts[entry.Built_form] = parseFloat(entry.Half_hourly_peak_heat_supplied_HPs) / magntitudesPeak[store.selectedSpatialResolution].factor
          })
          consumptionChart.data.datasets[0].label = 'Annual'
          consumptionChart.data.datasets[0].type = 'bar'
          consumptionChart.data.datasets[0].data = [
            annualConsumptionCounts.detached,
            annualConsumptionCounts['semi-detached'],
            annualConsumptionCounts.terraced,
            annualConsumptionCounts.flat
          ]
          consumptionChart.data.datasets[0].yAxisID = 'consumptionAnnual'
          consumptionChart.options.scales.consumptionAnnual.title.text = `Annual electricity consumption (${magntitudesAnnual[store.selectedSpatialResolution].unit})`
          consumptionChart.data.datasets[1].label = 'Half-hourly peak'
          consumptionChart.data.datasets[1].type = 'line'
          consumptionChart.data.datasets[1].data = [
            peakConsumptionCounts.detached,
            peakConsumptionCounts['semi-detached'],
            peakConsumptionCounts.terraced,
            peakConsumptionCounts.flat
          ]
          consumptionChart.data.datasets[1].yAxisID = 'consumptionPeak'
          consumptionChart.options.scales.consumptionPeak.title.text = `Peak electricity consumption (${magntitudesPeak[store.selectedSpatialResolution].unit})`
          consumptionChart.update()
          consumptionData.value = JSON.stringify(consumptionChart.data)

          heatSupplyChart.data.datasets[0].label = 'Annual'
          heatSupplyChart.data.datasets[0].type = 'bar'
          heatSupplyChart.data.datasets[0].data = [
            annualHeatDemandCounts.detached,
            annualHeatDemandCounts['semi-detached'],
            annualHeatDemandCounts.terraced,
            annualHeatDemandCounts.flat
          ]
          heatSupplyChart.data.datasets[0].yAxisID = 'consumptionAnnual'
          heatSupplyChart.options.scales.consumptionAnnual.title.text = `Annual heat supply (${magntitudesAnnual[store.selectedSpatialResolution].unit})`
          heatSupplyChart.data.datasets[1].label = 'Half-hourly peak'
          heatSupplyChart.data.datasets[1].type = 'line'
          heatSupplyChart.data.datasets[1].data = [
            peakHeatDemandCounts.detached,
            peakHeatDemandCounts['semi-detached'],
            peakHeatDemandCounts.terraced,
            peakHeatDemandCounts.flat
          ]
          heatSupplyChart.data.datasets[1].yAxisID = 'consumptionPeak'
          heatSupplyChart.options.scales.consumptionPeak.title.text = `Peak heat supply (${magntitudesPeak[store.selectedSpatialResolution].unit})`
          heatSupplyChart.update()
          heatSupplyData.value = JSON.stringify(heatSupplyChart.data)

          // Half hourly consumption chart
          halfHourlyConsumptionChart.data.labels = this.data.halfHourlyConsumption.map(entry => new Date(entry.timestamp).toLocaleDateString())
          halfHourlyConsumptionChart.data.datasets = [{
            label: 'Half-hourly consumption',
            data: this.data.halfHourlyConsumption.map(entry => entry.value / 1000)
          }]
          halfHourlyConsumptionChart.update()
          halfHourlyConsumptionData.value = JSON.stringify(halfHourlyConsumptionChart.data)

          // Half hourly supply chart
          halfHourlySupplyChart.data.labels = this.data.halfHourlyHeatSupply.map(entry => new Date(entry.timestamp).toLocaleDateString())
          halfHourlySupplyChart.data.datasets = [{
            label: 'Half-hourly heat supply',
            data: this.data.halfHourlyHeatSupply.map(entry => entry.value / 1000),
            borderColor: '#FF6384',
            backgroundColor: '#FF6384'
          }]
          halfHourlySupplyChart.update()
          halfHourlySupplyData.value = JSON.stringify(halfHourlySupplyChart.data)

          // Combined chart
          halfHourlyCombinedChart.data.labels = this.data.halfHourlyHeatSupply.map(entry => new Date(entry.timestamp).toLocaleDateString())
          halfHourlyCombinedChart.data.datasets = [{
            label: 'Electricity consumption',
            data: this.data.halfHourlyConsumption.map(entry => entry.value / 1000)
          },
          {
            label: 'Heat supply',
            data: this.data.halfHourlyHeatSupply.map(entry => entry.value / 1000)
          }]
          halfHourlyCombinedChart.update()
          halfHourlyCombinedData.value = JSON.stringify(halfHourlyCombinedChart.data)
        })
      }
    }
  })
})
